import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	alpha,
	Badge,
	Box,
	Button,
	Card,
	CardContent,
	Container,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	TextField,
	Tooltip,
	Typography,
	useTheme,
	Chip,
} from '@mui/material';
import {
	AccessibilityNew as PilatesIcon,
	CheckCircle as CheckIcon,
	Clear,
	Favorite as CircleOfCareIcon,
	FitnessCenter as FitnessIcon,
	Groups as GroupsIcon,
	Launch as LaunchIcon,
	PersonOutline as PersonIcon,
	Search,
	Timer as TimerIcon,
	TrendingDown as TrendingDownIcon,
	TrendingUp as TrendingUpIcon,
	Warning as WarningIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, QueryDocumentSnapshot, where, DocumentData } from 'firebase/firestore';
import { format, differenceInWeeks, isValid } from 'date-fns';
import { db } from '../../shared/utils/firebase';
import { toDate } from '../../shared/utils/dateHelpers';
import CustomLoader from '../../components/common/CustomLoader';
import { useAuth } from '../auth/useAuth';
import { managedLocations, whereLocation } from '../auth/managedLocations';

interface PrePostNatalClient {
	id: string;
	client_id: string;
	client_name: string;
	lead_coach_name: string;
	lead_coach_id: string;
	collaborating_coach_ids: string[];
	primary_location: string;
	current_programme: string;
	health_consult_date: string | null;
	first_pre_post_natal_date: string | null;
	first_session_date: string | null;
	pre_post_natal_pt_sessions: number;
	pre_post_natal_pilates_sessions: number;
	pre_post_natal_circle_of_care_sessions: number;
	pre_post_natal_expiration_date: string | null;
	is_pre_post_natal_client: boolean;
}

interface PrePostNatalRow extends PrePostNatalClient {
	first_session_sort_ts: number;
	expiration_sort_ts: number;
}

const LOG_PREFIX = '[pre-post-overview]';
const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 600;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const normalizeClientName = (name: string): string => {
	if (!name) return '';
	if (!name.includes(',')) return name;

	const [lastName, firstName] = name.split(',').map(part => part.trim());
	return `${firstName} ${lastName}`.trim();
};

const toFiniteNumber = (value: unknown, fallback = 0): number => {
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	if (typeof value === 'string') {
		const parsed = Number(value);
		if (Number.isFinite(parsed)) return parsed;
	}
	return fallback;
};

const toOptionalString = (value: unknown): string | null => {
	if (typeof value !== 'string') return null;
	const normalized = value.trim();
	return normalized.length ? normalized : null;
};

const executeWithRetry = async <T,>(operationName: string, operation: () => Promise<T>): Promise<T> => {
	let lastError: unknown;

	for (let attempt = 1; attempt <= MAX_RETRIES; attempt += 1) {
		try {
			console.info(`${LOG_PREFIX} ${operationName} attempt ${attempt}/${MAX_RETRIES} started`);
			const result = await operation();
			console.info(`${LOG_PREFIX} ${operationName} attempt ${attempt}/${MAX_RETRIES} succeeded`);
			return result;
		} catch (error) {
			lastError = error;
			console.error(`${LOG_PREFIX} ${operationName} attempt ${attempt}/${MAX_RETRIES} failed`, error);

			if (attempt < MAX_RETRIES) {
				const delay = RETRY_BASE_DELAY_MS * attempt;
				console.warn(`${LOG_PREFIX} ${operationName} retrying after ${delay}ms`);
				await sleep(delay);
			}
		}
	}

	throw lastError;
};

const mapDocToPrePostClient = (docSnap: QueryDocumentSnapshot<DocumentData>): PrePostNatalClient => {
	const data = docSnap.data();

	// Normalize mixed-type Firestore cache values into stable UI-safe types.
	return {
		id: docSnap.id,
		client_id: typeof data.client_id === 'string' ? data.client_id : docSnap.id,
		client_name: typeof data.client_name === 'string' ? data.client_name : '',
		lead_coach_name: typeof data.lead_coach_name === 'string' ? data.lead_coach_name : '',
		lead_coach_id: data.lead_coach_id != null ? String(data.lead_coach_id) : '',
		collaborating_coach_ids: Array.isArray(data.collaborating_coach_ids)
			? data.collaborating_coach_ids.map((coachId: unknown) => String(coachId))
			: [],
		primary_location: typeof data.primary_location === 'string' ? data.primary_location : '',
		current_programme: typeof data.current_programme === 'string' ? data.current_programme : '',
		health_consult_date: toOptionalString(data.health_consult_date),
		first_pre_post_natal_date: toOptionalString(data.first_pre_post_natal_date),
		first_session_date: toOptionalString(data.first_session_date),
		pre_post_natal_pt_sessions: toFiniteNumber(data.pre_post_natal_pt_sessions),
		pre_post_natal_pilates_sessions: toFiniteNumber(data.pre_post_natal_pilates_sessions),
		pre_post_natal_circle_of_care_sessions: toFiniteNumber(data.pre_post_natal_circle_of_care_sessions),
		pre_post_natal_expiration_date: toOptionalString(data.pre_post_natal_expiration_date),
		is_pre_post_natal_client: data.is_pre_post_natal_client === true,
	};
};

const getFirstSessionDateValue = (client: PrePostNatalClient): string | null => (
	client.first_pre_post_natal_date || client.first_session_date || null
);

const getDateTimestamp = (value: string | null): number => {
	if (!value) return 0;
	const parsedDate = toDate(value);
	if (!parsedDate || !isValid(parsedDate)) return 0;
	return parsedDate.getTime();
};

const isRoleSupported = (role: string | null): role is 'ADMIN' | 'MANAGER' | 'USER' => (
	role === 'ADMIN' || role === 'MANAGER' || role === 'USER'
);

const PrePostNatalOverview: React.FC = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const { userRole, userMetadata } = useAuth();

	const [clients, setClients] = useState<PrePostNatalClient[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [locationFilter, setLocationFilter] = useState('');
	const [coachFilter, setCoachFilter] = useState('');

	const uniqueLocations = useMemo(() => {
		const locations = new Set(clients.map(client => client.primary_location).filter(Boolean));
		return Array.from(locations).sort((a, b) => a.localeCompare(b));
	}, [clients]);

	const uniqueCoaches = useMemo(() => {
		const coaches = new Set(clients.map(client => client.lead_coach_name).filter(Boolean));
		return Array.from(coaches).sort((a, b) => a.localeCompare(b));
	}, [clients]);

	const activeFilterCount = useMemo(() => {
		let count = 0;
		if (searchTerm) count += 1;
		if (locationFilter) count += 1;
		if (coachFilter) count += 1;
		return count;
	}, [searchTerm, locationFilter, coachFilter]);

	const statistics = useMemo(() => {
		const now = new Date();
		let active = 0;
		let inactive = 0;
		let totalPtSessions = 0;
		let totalPilatesSessions = 0;
		let totalCircleOfCareSessions = 0;

		for (const client of clients) {
			totalPtSessions += client.pre_post_natal_pt_sessions;
			totalPilatesSessions += client.pre_post_natal_pilates_sessions;
			totalCircleOfCareSessions += client.pre_post_natal_circle_of_care_sessions;

			const expiryDate = toDate(client.pre_post_natal_expiration_date);
			if (!expiryDate || !isValid(expiryDate)) {
				inactive += 1;
				continue;
			}

			if (expiryDate >= now) {
				active += 1;
			} else {
				inactive += 1;
			}
		}

		return {
			total: clients.length,
			active,
			inactive,
			totalPtSessions,
			totalPilatesSessions,
			totalCircleOfCareSessions,
		};
	}, [clients]);

	const filteredClients = useMemo(() => {
		const normalizedSearch = searchTerm.trim().toLowerCase();

		return clients.filter(client => {
			const normalizedClientName = normalizeClientName(client.client_name).toLowerCase();
			const matchesSearch =
				normalizedSearch.length === 0 ||
				normalizedClientName.includes(normalizedSearch) ||
				client.client_id.toLowerCase().includes(normalizedSearch);
			const matchesLocation = !locationFilter || client.primary_location === locationFilter;
			const matchesCoach = !coachFilter || client.lead_coach_name === coachFilter;
			return matchesSearch && matchesLocation && matchesCoach;
		});
	}, [clients, coachFilter, locationFilter, searchTerm]);

	const rows = useMemo<PrePostNatalRow[]>(() => {
		return filteredClients.map(client => ({
			...client,
			first_session_sort_ts: getDateTimestamp(getFirstSessionDateValue(client)),
			expiration_sort_ts: getDateTimestamp(client.pre_post_natal_expiration_date),
		}));
	}, [filteredClients]);

	const clearFilters = () => {
		setSearchTerm('');
		setLocationFilter('');
		setCoachFilter('');
	};

	const navigateToClient = (clientId: string) => {
		if (!clientId) {
			console.warn(`${LOG_PREFIX} navigate skipped because client_id is missing`);
			return;
		}
		navigate(`/clients/${clientId}`);
	};

	useEffect(() => {
		let isUnmounted = false;

		const fetchClients = async () => {
			if (!userRole) {
				console.warn(`${LOG_PREFIX} missing user role; denying access to avoid undefined state`);
				setError('Unable to determine your access role.');
				setClients([]);
				setLoading(false);
				return;
			}

			if (!isRoleSupported(userRole)) {
				console.warn(`${LOG_PREFIX} unsupported role`, { userRole });
				setError('You do not have permission to view this page.');
				setClients([]);
				setLoading(false);
				return;
			}

			setLoading(true);
			setError(null);

			try {
				const clientsRef = collection(db, 'bigquery_cache/client_data/comprehensive_details');
				// Map by Firestore document id so USER lead/collaborator overlaps are deduped.
				const byId = new Map<string, QueryDocumentSnapshot<DocumentData>>();

				if (userRole === 'ADMIN') {
					console.info(`${LOG_PREFIX} loading scope=admin`);
					const snapshot = await executeWithRetry('fetch_admin_clients', () => getDocs(query(clientsRef)));
					snapshot.docs.forEach(docSnap => byId.set(docSnap.id, docSnap));
				}

				if (userRole === 'MANAGER') {
					const assignedLocations = managedLocations(userMetadata);
					if (assignedLocations.length === 0) {
						console.warn(`${LOG_PREFIX} manager has no assigned location`);
						setError('Manager account has no assigned location.');
						setClients([]);
						setLoading(false);
						return;
					}

					console.info(`${LOG_PREFIX} loading scope=manager`, { assignedLocations });
					const snapshot = await executeWithRetry('fetch_manager_clients', () => (
						getDocs(query(clientsRef, whereLocation('primary_location', assignedLocations)))
					));
					snapshot.docs.forEach(docSnap => byId.set(docSnap.id, docSnap));
				}

				if (userRole === 'USER') {
					const coachIdRaw = userMetadata?.coach_id;
					if (!coachIdRaw) {
						console.warn(`${LOG_PREFIX} user has no coach_id`);
						setError('Coach account has no coach_id configured.');
						setClients([]);
						setLoading(false);
						return;
					}

					const coachIdString = String(coachIdRaw);
					const coachIdNumber = Number(coachIdRaw);
					const hasNumericCoachId = !Number.isNaN(coachIdNumber);

					console.info(`${LOG_PREFIX} loading scope=user`, {
						coachIdString,
						hasNumericCoachId,
					});

					const leadCoachByStringQuery = query(clientsRef, where('lead_coach_id', '==', coachIdString));
					const leadCoachByNumberQuery = hasNumericCoachId
						? query(clientsRef, where('lead_coach_id', '==', coachIdNumber))
						: null;
					const collaboratorByStringQuery = query(clientsRef, where('collaborating_coach_ids', 'array-contains', coachIdString));
					const collaboratorByNumberQuery = hasNumericCoachId
						? query(clientsRef, where('collaborating_coach_ids', 'array-contains', coachIdNumber))
						: null;

					// Fetch string and numeric coach variants to support mixed legacy schemas.
					const [
						leadByStringSnapshot,
						leadByNumberSnapshot,
						collaboratorByStringSnapshot,
						collaboratorByNumberSnapshot,
					] = await Promise.all([
						executeWithRetry('fetch_user_lead_clients_string', () => getDocs(leadCoachByStringQuery)),
						leadCoachByNumberQuery
							? executeWithRetry('fetch_user_lead_clients_number', () => getDocs(leadCoachByNumberQuery))
							: Promise.resolve(null),
						executeWithRetry('fetch_user_collaborator_clients_string', () => getDocs(collaboratorByStringQuery)),
						collaboratorByNumberQuery
							? executeWithRetry('fetch_user_collaborator_clients_number', () => getDocs(collaboratorByNumberQuery))
							: Promise.resolve(null),
					]);

					leadByStringSnapshot.docs.forEach(docSnap => byId.set(docSnap.id, docSnap));
					leadByNumberSnapshot?.docs.forEach(docSnap => byId.set(docSnap.id, docSnap));
					collaboratorByStringSnapshot.docs.forEach(docSnap => byId.set(docSnap.id, docSnap));
					collaboratorByNumberSnapshot?.docs.forEach(docSnap => byId.set(docSnap.id, docSnap));
				}

				const prePostClients = Array.from(byId.values())
					.map(mapDocToPrePostClient)
					.filter(client => client.is_pre_post_natal_client)
					.sort((a, b) => a.client_name.localeCompare(b.client_name));

				console.info(`${LOG_PREFIX} final client set prepared`, {
					role: userRole,
					totalScopedClients: byId.size,
					totalPrePostClients: prePostClients.length,
				});

				if (!isUnmounted) {
					setClients(prePostClients);
				}
			} catch (fetchError) {
				console.error(`${LOG_PREFIX} failed to load pre/post clients`, fetchError);
				if (!isUnmounted) {
					setError('Failed to load Pre/Post Natal clients. Please try again.');
					setClients([]);
				}
			} finally {
				if (!isUnmounted) {
					setLoading(false);
				}
			}
		};

		fetchClients();

		return () => {
			isUnmounted = true;
			console.info(`${LOG_PREFIX} component unmounted; pending state updates cancelled`);
		};
	}, [userMetadata, userRole]);

	const columns: GridColDef[] = [
		{
			field: 'client_name',
			headerName: 'Client',
			flex: 1.6,
			minWidth: 220,
			renderCell: (params: GridRenderCellParams<PrePostNatalRow>) => (
				<Box>
					<Typography variant="body2" fontWeight={600}>
						{normalizeClientName(params.row.client_name)}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						ID: {params.row.client_id}
					</Typography>
				</Box>
			),
		},
		{
			field: 'lead_coach_name',
			headerName: 'Coach',
			flex: 1,
			minWidth: 170,
			renderCell: (params: GridRenderCellParams<PrePostNatalRow>) => (
				<Box display="flex" alignItems="center" gap={1}>
					<PersonIcon fontSize="small" color="action" />
					<Typography variant="body2">{params.row.lead_coach_name || 'Not assigned'}</Typography>
				</Box>
			),
		},
		{
			field: 'primary_location',
			headerName: 'Location',
			flex: 1,
			minWidth: 160,
			valueGetter: params => params.row.primary_location || '-',
		},
		{
			field: 'first_session_sort_ts',
			headerName: 'First Session',
			flex: 1,
			minWidth: 170,
			renderCell: (params: GridRenderCellParams<PrePostNatalRow>) => {
				const firstSessionDate = getFirstSessionDateValue(params.row);
				const firstSessionDateObject = toDate(firstSessionDate);

				if (!firstSessionDate || !firstSessionDateObject || !isValid(firstSessionDateObject)) {
					return <Typography variant="body2" color="text.secondary">-</Typography>;
				}

				const weeksInProgramme = differenceInWeeks(new Date(), firstSessionDateObject);

				return (
					<Box>
						<Typography variant="body2">{format(firstSessionDateObject, 'dd/MM/yyyy')}</Typography>
						<Typography variant="caption" color="text.secondary">
							{weeksInProgramme} weeks
						</Typography>
					</Box>
				);
			},
		},
		{
			field: 'sessions',
			headerName: 'Sessions',
			flex: 1.8,
			minWidth: 280,
			sortable: false,
			renderCell: (params: GridRenderCellParams<PrePostNatalRow>) => (
				<Box display="flex" gap={0.5} flexWrap="wrap">
					<Tooltip title="PT Sessions">
						<Chip
							icon={<FitnessIcon fontSize="small" />}
							label={params.row.pre_post_natal_pt_sessions}
							size="small"
							variant="outlined"
						/>
					</Tooltip>
					<Tooltip title="Pilates Sessions">
						<Chip
							icon={<PilatesIcon fontSize="small" />}
							label={params.row.pre_post_natal_pilates_sessions}
							size="small"
							variant="outlined"
						/>
					</Tooltip>
					<Tooltip title="Circle of Care Sessions">
						<Chip
							icon={<CircleOfCareIcon fontSize="small" />}
							label={params.row.pre_post_natal_circle_of_care_sessions}
							size="small"
							variant="outlined"
							sx={{
								bgcolor: alpha(theme.palette.primary.main, 0.06),
							}}
						/>
					</Tooltip>
				</Box>
			),
		},
		{
			field: 'expiration_sort_ts',
			headerName: 'Expiration',
			flex: 1,
			minWidth: 170,
			renderCell: (params: GridRenderCellParams<PrePostNatalRow>) => {
				const expirationDate = toDate(params.row.pre_post_natal_expiration_date);
				if (!expirationDate || !isValid(expirationDate)) {
					return <Typography variant="body2" color="text.secondary">-</Typography>;
				}

				const weeksLeft = differenceInWeeks(expirationDate, new Date());
				const isExpired = weeksLeft < 0;
				const isExpiringSoon = !isExpired && weeksLeft < 4;

				return (
					<Box display="flex" alignItems="center" gap={1}>
						<Box>
							<Typography variant="body2">{format(expirationDate, 'dd/MM/yyyy')}</Typography>
							<Typography
								variant="caption"
								color={isExpired ? 'error' : isExpiringSoon ? 'warning.main' : 'text.secondary'}
							>
								{isExpired ? 'Expired' : `${weeksLeft} weeks left`}
							</Typography>
						</Box>
						{isExpired ? (
							<WarningIcon color="error" fontSize="small" />
						) : isExpiringSoon ? (
							<TimerIcon color="warning" fontSize="small" />
						) : (
							<CheckIcon color="success" fontSize="small" />
						)}
					</Box>
				);
			},
		},
		{
			field: 'actions',
			headerName: 'Actions',
			width: 120,
			sortable: false,
			renderCell: (params: GridRenderCellParams<PrePostNatalRow>) => (
				<Button
					variant="outlined"
					size="small"
					startIcon={<LaunchIcon />}
					onClick={event => {
						event.stopPropagation();
						navigateToClient(params.row.client_id);
					}}
					sx={{ textTransform: 'none' }}
				>
					View
				</Button>
			),
		},
	];

	if (userRole && !isRoleSupported(userRole)) {
		return (
			<Container maxWidth="md" sx={{ mt: 4 }}>
				<Alert severity="error">You do not have permission to view this page.</Alert>
			</Container>
		);
	}

	return (
		<Box sx={{ p: 3, maxWidth: 1450, mx: 'auto' }}>
			<motion.div
				initial={{ opacity: 0, y: 16 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.45 }}
			>
				<Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
					Pre/Post Natal Overview
				</Typography>
				<Typography variant="body1" color="text.secondary" paragraph>
					Track and manage Pre/Post Natal programme clients
				</Typography>

				<Grid container spacing={3} sx={{ mb: 4 }}>
					<Grid item xs={12} sm={6} md={3}>
						<Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`, borderRadius: 3 }}>
							<CardContent>
								<Box display="flex" justifyContent="space-between" alignItems="flex-start">
									<Box>
										<Typography variant="overline" color="text.secondary" fontWeight={600}>Total Clients</Typography>
										<Typography variant="h4" fontWeight={700}>{statistics.total}</Typography>
									</Box>
									<GroupsIcon color="primary" />
								</Box>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`, borderRadius: 3 }}>
							<CardContent>
								<Box display="flex" justifyContent="space-between" alignItems="flex-start">
									<Box>
										<Typography variant="overline" color="text.secondary" fontWeight={600}>Active</Typography>
										<Typography variant="h4" fontWeight={700} color="success.main">{statistics.active}</Typography>
									</Box>
									<TrendingUpIcon color="success" />
								</Box>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`, borderRadius: 3 }}>
							<CardContent>
								<Box display="flex" justifyContent="space-between" alignItems="flex-start">
									<Box>
										<Typography variant="overline" color="text.secondary" fontWeight={600}>Inactive</Typography>
										<Typography variant="h4" fontWeight={700} color="error.main">{statistics.inactive}</Typography>
									</Box>
									<TrendingDownIcon color="error" />
								</Box>
							</CardContent>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card elevation={0} sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`, borderRadius: 3 }}>
							<CardContent>
								<Box display="flex" justifyContent="space-between" alignItems="flex-start">
									<Box>
										<Typography variant="overline" color="text.secondary" fontWeight={600}>Total Sessions</Typography>
										<Typography variant="h4" fontWeight={700} color="primary.main">
											{statistics.totalPtSessions + statistics.totalPilatesSessions + statistics.totalCircleOfCareSessions}
										</Typography>
									</Box>
									<CircleOfCareIcon color="primary" />
								</Box>
							</CardContent>
						</Card>
					</Grid>
				</Grid>

				<Grid container spacing={3}>
					<Grid item xs={12}>
						<Paper elevation={1} sx={{ p: 2.5, borderRadius: 3 }}>
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={12} md={6}>
									<TextField
										fullWidth
										placeholder="Search by client name or ID"
										value={searchTerm}
										onChange={event => setSearchTerm(event.target.value)}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<Search color="action" />
												</InputAdornment>
											),
										}}
									/>
								</Grid>
								<Grid item xs={12} md={2}>
									<FormControl fullWidth>
										<InputLabel>Location</InputLabel>
										<Select
											value={locationFilter}
											onChange={event => setLocationFilter(event.target.value)}
											label="Location"
										>
											<MenuItem value="">All Locations</MenuItem>
											{uniqueLocations.map(location => (
												<MenuItem key={location} value={location}>{location}</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
								<Grid item xs={12} md={3}>
									<FormControl fullWidth>
										<InputLabel>Coach</InputLabel>
										<Select
											value={coachFilter}
											onChange={event => setCoachFilter(event.target.value)}
											label="Coach"
										>
											<MenuItem value="">All Coaches</MenuItem>
											{uniqueCoaches.map(coach => (
												<MenuItem key={coach} value={coach}>{coach}</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>
								<Grid item xs={12} md={1}>
									<Badge badgeContent={activeFilterCount} color="primary">
										<IconButton onClick={clearFilters} disabled={activeFilterCount === 0} color="primary">
											<Clear />
										</IconButton>
									</Badge>
								</Grid>
							</Grid>
						</Paper>
					</Grid>

					<Grid item xs={12}>
						<Card elevation={2}>
							<CardContent sx={{ p: 0 }}>
								{loading ? (
									<Box display="flex" justifyContent="center" alignItems="center" minHeight={340}>
										<CustomLoader />
									</Box>
								) : error ? (
									<Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
								) : (
									<DataGrid
										rows={rows}
										columns={columns}
										onRowClick={(params: GridRowParams<PrePostNatalRow>) => navigateToClient(params.row.client_id)}
										initialState={{
											pagination: {
												paginationModel: { pageSize: 10 },
											},
											sorting: {
												sortModel: [{ field: 'client_name', sort: 'asc' }],
											},
										}}
										pageSizeOptions={[10, 25, 50]}
										disableRowSelectionOnClick
										autoHeight
										sx={{
											border: 'none',
											'& .MuiDataGrid-cell': {
												borderBottom: `1px solid ${alpha(theme.palette.divider, 0.28)}`,
												py: 2,
											},
											'& .MuiDataGrid-columnHeaders': {
												backgroundColor: alpha(theme.palette.primary.main, 0.06),
												borderBottom: `2px solid ${theme.palette.primary.main}`,
											},
											'& .MuiDataGrid-row': {
												cursor: 'pointer',
												'&:hover': {
													backgroundColor: alpha(theme.palette.primary.main, 0.03),
												},
											},
											'& .MuiDataGrid-footerContainer': {
												borderTop: `2px solid ${theme.palette.divider}`,
											},
										}}
									/>
								)}
							</CardContent>
						</Card>
					</Grid>
				</Grid>
			</motion.div>
		</Box>
	);
};

export default PrePostNatalOverview;
